import differenceInDays from 'date-fns/differenceInDays'
import format from 'date-fns/format'
import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import isSameYear from 'date-fns/isSameYear'
import isWithinInterval from 'date-fns/isWithinInterval'
import getISODay from 'date-fns/getISODay'
import getMonth from 'date-fns/getMonth'
import subDays from 'date-fns/subDays'

import Chartist from 'chartist'

import { DAY_NAMES, MONTH_NAMES } from 'constants/stats'

// Variables used for animations
let delays = 80
let durations = 500
let delays2 = 80
let durations2 = 500

export const getDashboardStats = ({ courses, providers }) => {
  // Today for other calculations
  const now = new Date()

  const yesterday = subDays(now, 1)
  const yesterdayWeekAgo = subDays(now, 8)

  const aList = []

  // Totals for last 7 days
  const wStats = eachDayOfInterval({
    start: yesterdayWeekAgo,
    end: yesterday
  }).map(day => ({
    name: DAY_NAMES[getISODay(day) - 1],
    attendees: 0
  }))

  // Totals for month
  const mStats = MONTH_NAMES.map(name => ({
    name,
    attendees: 0,
    courses: 0,
    providers: 0,
    providerNames: {}
  }))

  // Provider stats
  const pStats = {}

  // Totals for year
  const tStats = {
    attendees: 0,
    courses: 0,
    coursesRecorded: 0,
    providers: 0
  }

  Object.values(courses)
    .filter(course => isSameYear(course.date, now))
    .forEach(course => {
      const month = getMonth(course.date)
      const provider = providers[course.provider]
      const attendees = Object.keys(course.attendees || {}).length
      const attendeeList = Object.values(course.attendees || {})
      const cancelled = Boolean(course.cancelled)

      attendeeList.forEach(({ signature, ...attendee }) => {
        aList.push({
          date: course.date,
          provider: provider.name,
          course: course.title,
          ...attendee
        })
      })

      // Check for inclusion in last week stats
      if (
        isWithinInterval(course.date, {
          start: yesterdayWeekAgo,
          end: yesterday
        })
      ) {
        wStats[
          7 - differenceInDays(yesterday, course.date)
        ].attendees += attendees
      }

      // If the provider doesn't have stats yet, create the base for them
      if (!pStats.hasOwnProperty(provider.name)) {
        tStats.providers++
        pStats[provider.name] = {
          color: provider.color,
          attendees: 0,
          courses: 0,
          coursesCancelled: 0,
          coursesRecorded: 0
        }
      }

      // If the provider isn't already in the month's list
      if (!mStats[month].providerNames.hasOwnProperty(provider.name)) {
        mStats[month].providerNames[provider.name] = true
      }

      // Add values to provider stats
      pStats[provider.name].attendees += attendees
      pStats[provider.name].courses++
      pStats[provider.name].coursesCancelled += cancelled ? 1 : 0
      pStats[provider.name].coursesRecorded += attendees ? 1 : 0

      // Add values to month stats
      mStats[month].attendees += attendees
      mStats[month].courses++

      // Add values to total stats
      tStats.attendees += attendees
      tStats.courses++
      tStats.coursesRecorded += attendees ? 1 : 0
    })

  return {
    attendeeList: aList
      .sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        return dateA > dateB
          ? 1
          : dateA < dateB
            ? -1
            : a.firstName > b.firstName
              ? 1
              : -1
      })
      .map(a => ({
        ...a,
        date: format(a.date, 'DD/MM/YYYY')
      })),
    weekStats: wStats,
    monthStats: mStats.map(m => ({
      ...m,
      providers: Object.keys(m.providerNames).length
    })),
    providerStats: pStats,
    totalStats: tStats
  }
}

/*
 * Monthly Courses Chart
 */
export const monthlyCoursesChart = mStats => {
  const mCourses = mStats.map(m => m.courses)
  return {
    data: {
      labels: MONTH_NAMES,
      series: [mCourses]
    },
    options: {
      axisX: {
        showGrid: false
      },
      low: 0,
      high: Math.max(...mCourses) + 5,
      chartPadding: {
        top: 0,
        right: 5,
        bottom: 0,
        left: 0
      }
    },
    responsiveOptions: [
      [
        'screen and (max-width: 640px)',
        {
          seriesBarDistance: 5,
          axisX: {
            labelInterpolationFnc: function(value) {
              return value[0]
            }
          }
        }
      ]
    ],
    animation: {
      draw: data => {
        if (data.type === 'bar') {
          data.element.animate({
            opacity: {
              begin: (data.index + 1) * delays2,
              dur: durations2,
              from: 0,
              to: 1,
              easing: 'ease'
            }
          })
        }
      }
    }
  }
}

/*
 * Daily Attendees Chart
 */
export const dailyAttendeesChart = wStats => {
  const wAttendees = wStats.map(d => d.attendees)
  return {
    data: {
      labels: wStats.map(d => d.name),
      series: [wAttendees]
    },
    options: {
      lineSmooth: Chartist.Interpolation.cardinal({
        tension: 0
      }),
      low: 0,
      high: Math.max(...wAttendees) + 5,
      chartPadding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
    animation: {
      draw: function(data) {
        if (data.type === 'line' || data.type === 'area') {
          data.element.animate({
            d: {
              begin: 600,
              dur: 700,
              from: data.path
                .clone()
                .scale(1, 0)
                .translate(0, data.chartRect.height())
                .stringify(),
              to: data.path.clone().stringify(),
              easing: Chartist.Svg.Easing.easeOutQuint
            }
          })
        } else if (data.type === 'point') {
          data.element.animate({
            opacity: {
              begin: (data.index + 1) * delays,
              dur: durations,
              from: 0,
              to: 1,
              easing: 'ease'
            }
          })
        }
      }
    }
  }
}

/*
 * Top Provider Table
 */
export const topProviderTable = pStats => ({
  columns: [
    'Name',
    'Courses Booked',
    'Courses Cancelled',
    'Courses Recorded',
    'Attendees Registered'
  ],
  rows: Object.entries(pStats)
    .map(
      ([name, { attendees, courses, coursesCancelled, coursesRecorded }]) => [
        String(name),
        String(courses),
        String(coursesCancelled),
        String(coursesRecorded),
        String(attendees)
      ]
    )
    .sort((a, b) => (a.courses > b.courses ? 1 : -1))
    .filter((p, i) => i < 5)
})



// WEBPACK FOOTER //
// ./src/views/Dashboard/Main/helpers/stats.js